import React, {useEffect, useState} from "react";
import Select from "react-select";
import countries from "../data/countries.json"; // Import the country list (legacy format)
import countriesWithCodes from "../data/countries-with-codes.json"; // Import the country list with codes
import states from "../data/states.json"; // Import the states list (now with codes)
import highSchools from "../data/high-schools.json"; // Import the high schools list with CEEB codes

const fieldOptions = [
    {value: "input", label: "Short Text"},
    {value: "student_bio", label: "Basic Info"},
    {value: "citizenship", label: "Citizenship"},
    {value: "country", label: "Country"},
    {value: "states", label: "State"},
    {value: "high_school", label: "High School"},
    {value: "select", label: "Dropdown"},
    {value: "date", label: "Date"},
    {value: "email", label: "Email"},
    {value: "radio", label: "Single Choice"},
    {value: "phone", label: "Phone"},
    {value: "display_text", label: "Display Text"},
];

const AddFieldModal = ({onSave, onClose}) => {
    const [fieldType, setFieldType] = useState(fieldOptions[0]); // Default to first option
    const [fieldName, setFieldName] = useState("");
    const [required, setRequired] = useState(false);
    const [options, setOptions] = useState([]); // Will store objects with name/value pairs
    const [manuallyEditedValues, setManuallyEditedValues] = useState(new Set()); // Track which system values were manually edited
    const [displayText, setDisplayText] = useState(""); // For display_text field type

    const addOption = () => {
        setOptions([...options, { name: "", value: "" }]);
    };

    const updateOption = (index, field, value) => {
        const updatedOptions = [...options];
        updatedOptions[index] = { ...updatedOptions[index], [field]: value };
        
        // If user manually edits system value, mark it as manually edited
        if (field === 'value') {
            setManuallyEditedValues(prev => {
                const newSet = new Set(prev);
                newSet.add(index);
                return newSet;
            });
        }
        
        setOptions(updatedOptions);
    };

    const updateOptionName = (index, name) => {
        const updatedOptions = [...options];
        const currentOption = updatedOptions[index];
        
        // Only auto-copy if system value hasn't been manually edited
        const shouldAutoCopy = !manuallyEditedValues.has(index) || !currentOption.value;
        const newValue = shouldAutoCopy ? name : currentOption.value;
        
        updatedOptions[index] = { 
            name: name, 
            value: newValue
        };
        
        setOptions(updatedOptions);
    };

    // Function to clear system value and re-enable auto-copy
    const clearSystemValue = (index) => {
        const updatedOptions = [...options];
        updatedOptions[index] = { 
            ...updatedOptions[index], 
            value: "" 
        };
        setOptions(updatedOptions);
        setManuallyEditedValues(prev => {
            const newSet = new Set(prev);
            newSet.delete(index);
            return newSet;
        });
    };

    // Student Bio predefined fields
    const studentBioFields = [
        {name: "First Name", type: "input", required: true},
        {name: "Last Name", type: "input", required: true},
        {name: "Date of Birth", type: "date", required: true},
        {name: "Email", type: "email", required: true},
        {name: "Phone", type: "phone", required: false},
    ];

    useEffect(() => {
        if (fieldType.value === "citizenship") {
            setFieldName("Citizenship");
            setRequired(true);
            setOptions([
                { name: "Please select a country", value: "" },
                ...countries.map((c) => ({ name: c.name, value: c.name }))
            ]); // Prepopulate country list with default option
        } else if (fieldType.value === "country") {
            setFieldName("Country");
            setRequired(true);
            setOptions([
                { name: "Please select a country", value: "" },
                ...countriesWithCodes.map((c) => ({ name: c.name, value: c.code }))
            ]); // Prepopulate country list with codes
        } else if (fieldType.value === "states") {
            setFieldName("State");
            setRequired(true);
            setOptions([
                { name: "Please select a state", value: "" },
                ...states.map((s) => ({ name: s.name, value: s.code }))
            ]); // Prepopulate states list with codes
        } else if (fieldType.value === "high_school") {
            setFieldName("High School");
            setRequired(true);
            setOptions([
                { name: "Please select a high school", value: "" },
                ...highSchools.map((school) => ({ name: school.name, value: school.code }))
            ]); // Prepopulate high schools list with CEEB codes
        } else if (fieldType.value === "student_bio") {
            setFieldName("Student Bio");
            setRequired(true);
        } else {
            setFieldName("");
            setRequired(false);
            setOptions([]);
        }
    }, [fieldType]);

    const saveField = () => {
        if (!fieldName.trim()) {
            alert("Field Name is required");
            return;
        }

        if (fieldType.value === "display_text") {
            if (!displayText.trim()) {
                alert("Display Text is required");
                return;
            }
            const newField = {
                type: "display_text",
                name: fieldName,
                required: false, // Display text fields are never required
                text: displayText,
            };
            onSave([newField]);
        } else if (fieldType.value === "student_bio") {
            onSave(studentBioFields);
        } else {
            // Process options to ensure they have both name and value
            let processedOptions = null;
            if (fieldType.value === "select" || fieldType.value === "radio" || fieldType.value === "citizenship" || fieldType.value === "country" || fieldType.value === "states" || fieldType.value === "high_school") {
                processedOptions = options.map(option => {
                    if (typeof option === 'string') {
                        // Legacy format - convert to new format
                        return { name: option, value: option };
                    } else if (option && typeof option === 'object') {
                        // New format - ensure both name and value exist
                        return {
                            name: option.name || "",
                            value: option.value || option.name || ""
                        };
                    }
                    return { name: "", value: "" };
                }).filter(option => option.name.trim() !== ""); // Remove empty options
            }

            const newField = {
                type: fieldType.value === "citizenship" || fieldType.value === "country" || fieldType.value === "states" || fieldType.value === "high_school" ? "select" : fieldType.value,
                name: fieldName,
                required,
                options: processedOptions,
            };
            onSave([newField]);
        }

        setFieldName("");
        setFieldType(fieldOptions[0]); // Reset to default field type
        setRequired(false);
        setOptions([]);
        setManuallyEditedValues(new Set());
        setDisplayText("");
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "5px",
                    width: "400px",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                }}
            >
                <h2>Add Field</h2>
                <div className="form-group">
                    <label>Field Type:</label>
                    <Select options={fieldOptions} value={fieldType} onChange={setFieldType} isSearchable={false}/>
                </div>
                <div className="form-group">
                    <label>Field Name:</label>
                    <input
                        type="text"
                        className="form-control"
                        value={fieldName}
                        onChange={(e) => setFieldName(e.target.value)}
                        disabled={fieldType.value === "student_bio"}
                    />
                </div>
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={required}
                            onChange={(e) => setRequired(e.target.checked)}
                            disabled={fieldType.value === "citizenship" || fieldType.value === "country" || fieldType.value === "states" || fieldType.value === "high_school" || fieldType.value === "student_bio"}
                        />
                        Required
                    </label>
                </div>
                {(fieldType.value === "select" || fieldType.value === "radio") && (
                    <div className="form-group">
                        <label>Options:</label>
                        <div
                            style={{
                                maxHeight: "200px", // Increased height for two fields
                                overflowY: "auto", // Enable vertical scrolling
                                padding: "5px",
                                borderRadius: "5px",
                                border: "1px solid #ddd",
                            }}
                        >
                            {options.map((option, index) => (
                                <div
                                    key={index} 
                                    style={{
                                        display: "flex", 
                                        alignItems: "center", 
                                        marginBottom: "10px",
                                        padding: "5px",
                                        border: "1px solid #eee",
                                        borderRadius: "3px"
                                    }}
                                >
                                    <div style={{ flex: 1, marginRight: "10px" }}>
                                        <label style={{ fontSize: "12px", color: "#666", marginBottom: "2px" }}>
                                            Display Name:
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g., Nevada"
                                            value={option.name || ""}
                                            onChange={(e) => updateOptionName(index, e.target.value)}
                                            style={{ fontSize: "12px" }}
                                        />
                                    </div>
                                    <div style={{ flex: 1, marginRight: "10px" }}>
                                        <label style={{ fontSize: "12px", color: "#666", marginBottom: "2px" }}>
                                            System Value:
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g., NE"
                                            value={option.value || ""}
                                            onChange={(e) => updateOption(index, "value", e.target.value)}
                                            style={{ fontSize: "12px" }}
                                        />
                                    </div>
                                    <button 
                                        className="btn btn-default clone-btn-right delete mb-1" 
                                        type="button"
                                        onClick={() => setOptions(options.filter((_, i) => i !== index))}
                                        style={{ marginTop: "20px" }}
                                    >
                                        -
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button type="button" className="btn btn-sm btn-info" onClick={addOption}>
                            + Add Option
                        </button>
                        <small style={{ color: "#666", display: "block", marginTop: "5px" }}>
                            💡 <strong>Display Name</strong> is what users see. <strong>System Value</strong> is what gets sent to external systems.
                            <br/>System Value automatically copies from Display Name. Once manually edited, auto-copy stops for that option.
                        </small>
                    </div>
                )}
                                        {(fieldType.value === "citizenship" || fieldType.value === "country") && (
                    <div className="form-group">
                        <label>Country List:</label>
                        <input type="text" className="form-control" value="Predefined" disabled/>
                    </div>
                )}
                {fieldType.value === "states" && (
                    <div className="form-group">
                        <label>State List:</label>
                        <input type="text" className="form-control" value="Predefined" disabled/>
                    </div>
                )}
                {fieldType.value === "high_school" && (
                    <div className="form-group">
                        <label>High School List:</label>
                        <input type="text" className="form-control" value="Predefined" disabled/>
                    </div>
                )}
                {fieldType.value === "student_bio" && (
                    <div className="form-group">
                        <label>Fields in Basic Info:</label>
                        <ul style={{listStyleType: "none", padding: 0, fontSize: "14px", color: "#555"}}>
                            {studentBioFields.map((field, index) => (
                                <li key={index}>
                                    <strong>{field.name}</strong> ({field.type}) {field.required ? "(Required)" : "(Optional)"}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {fieldType.value === "display_text" && (
                    <div className="form-group">
                        <label>Display Text:</label>
                        <textarea
                            className="form-control"
                            value={displayText}
                            onChange={(e) => setDisplayText(e.target.value)}
                            rows={4}
                            placeholder="Enter the text to display to users. Use [link]Link Text|https://example.com[/link] for clickable links."
                        />
                        <div style={{marginTop: "8px", fontSize: "12px", color: "#666"}}>
                            💡 This field displays text to users but won't be included in form submissions. 
                            To add clickable links, use this format: [link]Link Text|https://example.com[/link]
                        </div>
                    </div>
                )}
                <div style={{marginTop: "20px", textAlign: "right"}}>
                    <button type="button" className="btn btn-primary" onClick={saveField} style={{marginRight: "10px"}}>
                        Save
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddFieldModal;